import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";

import { ExperienceStatus } from "@intelligems/ig-types";
import { Box, Grid, styled } from "@mui/material";
import { gridTheme } from "@src/components/MainApp/SharedProductPicker/gridTheme";
import { SelectedProductRenderParams } from "@src/components/MainApp/SharedProductPicker/types";
import { useSelector } from "@src/store";
import {
  areProductPickerSelectedProductsLoading,
  getProductPickerSelectedProducts,
} from "@src/store/slices/productPicker";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";

import { ProductListDeleteButton } from "./ProductListDeleteButton";
import { ProductListImage } from "./ProductListImage";
import { ProductListText } from "./ProductListText";

interface SelectedProductListProps {
  isView?: boolean;
  experienceStatus?: ExperienceStatus;
  showToolTipText?: boolean;
}

export const SelectedProductList = (props: SelectedProductListProps) => {
  const selectedProducts = useSelector(getProductPickerSelectedProducts);
  const loading = useSelector(areProductPickerSelectedProductsLoading);
  const columnDefs = useColumnDefs(props);

  return (
    <Grid item sx={{ height: "365px" }}>
      <AgGridReact<ProductPickerSelectedProduct>
        theme={theme}
        loading={loading}
        rowData={selectedProducts}
        headerHeight={0}
        columnDefs={columnDefs}
        suppressAggFuncInHeader={true}
        groupDisplayType={"groupRows"}
        rowHeight={60}
        rowSelection={{
          mode: "singleRow",
          enableClickSelection: false,
          checkboxes: false,
        }}
        suppressCellFocus={true}
        cellSelection={false}
        stopEditingWhenCellsLoseFocus={true}
        undoRedoCellEditing={true}
        undoRedoCellEditingLimit={25}
        suppressMenuHide={false}
        enableBrowserTooltips={true}
        suppressHorizontalScroll={true}
      />
    </Grid>
  );
};

const Container = styled(Box)({
  height: "365px",
  // width: "100px",
  "& .ag-body-viewport": {
    overscrollBehavior: "contain",
  },
  "& .ag-root-wrapper": {
    borderRadius: 0,
  },
});

function useColumnDefs({
  isView,
  experienceStatus,
  showToolTipText,
}: SelectedProductListProps) {
  return useMemo((): ColDef<ProductPickerSelectedProduct>[] => {
    const cellRendererParams: SelectedProductRenderParams = {
      isView,
      experienceStatus,
      showToolTipText,
    };
    const defaultDef = {
      cellRendererParams,
      resizable: false,
      suppressHeaderMenuButton: true,
    };
    return [
      {
        ...defaultDef,
        width: 60,
        cellRenderer: ProductListImage,
      },
      {
        ...defaultDef,
        headerName: "Product",
        flex: 1,
        cellRenderer: ProductListText,
      },
      {
        ...defaultDef,
        width: 30,
        minWidth: 30,
        cellRenderer: ProductListDeleteButton,
      },
    ];
  }, [isView, experienceStatus]);
}

const theme = gridTheme.withParams({
  rowBorder: false,
  headerRowBorder: false,
});
